import React, { useState, useCallback, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { FiX, FiUpload } from 'react-icons/fi';
import { MAX_FILE_SIZE, formatFileSize } from '../../types/deck';

// Type cast icons for React 18 compatibility
const CloseIcon = FiX as React.FC<React.SVGProps<SVGSVGElement>>;
const UploadIcon = FiUpload as React.FC<React.SVGProps<SVGSVGElement>>;

// Supported MIME types for deck uploads
const SUPPORTED_MIME_TYPES: Record<string, boolean> = {
  'application/pdf': true,
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': true,
  'application/vnd.ms-powerpoint': true,
  'application/msword': true,
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': true,
  'image/png': true,
  'image/jpeg': true,
};

interface UploadDeckModalProps {
  onClose: () => void;
  onSubmit: (file: File) => Promise<void>;
}

const UploadDeckModal: React.FC<UploadDeckModalProps> = ({
  onClose,
  onSubmit,
}) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget && !isUploading) {
      onClose();
    }
  };

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isUploading) {
        onClose();
      }
    },
    [isUploading, onClose]
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);

  const validateFile = (file: File): string | null => {
    if (!SUPPORTED_MIME_TYPES[file.type]) {
      return 'Unsupported file type. Please upload PDF, PowerPoint, Word, PNG, or JPG files.';
    }
    if (file.size > MAX_FILE_SIZE) {
      return `File size exceeds maximum limit of ${formatFileSize(MAX_FILE_SIZE)}.`;
    }
    return null;
  };

  const handleFileSelect = (file: File) => {
    setError(null);
    const validationError = validateFile(file);
    if (validationError) {
      setError(validationError);
      return;
    }
    setSelectedFile(file);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDropzoneClick = () => {
    fileInputRef.current?.click();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!selectedFile) {
      setError('Please select a file to upload');
      return;
    }

    setIsUploading(true);

    try {
      await onSubmit(selectedFile);
      onClose();
    } catch (err: any) {
      setError(err?.message || 'Failed to upload deck');
    } finally {
      setIsUploading(false);
    }
  };

  const modal = (
    <div className="su-deck-modal-overlay" onClick={handleOverlayClick}>
      <div className="su-deck-modal" style={{ maxWidth: '480px' }} role="dialog" aria-modal="true" aria-labelledby="upload-deck-title">
        <div className="su-deck-modal-header">
          <div>
            <h2 id="upload-deck-title" className="su-deck-modal-title">Upload Pitch Deck</h2>
            <p className="su-deck-modal-subtitle">
              Share your presentation with investors
            </p>
          </div>
          <button
            className="su-deck-modal-close"
            onClick={onClose}
            disabled={isUploading}
            aria-label="Close modal"
          >
            <CloseIcon width={20} height={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="su-deck-modal-body">
            {error && (
              <div className="su-deck-error-message">
                {error}
              </div>
            )}

            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.pptx,.ppt,.doc,.docx,.png,.jpg,.jpeg,application/pdf,application/vnd.openxmlformats-officedocument.presentationml.presentation,application/vnd.ms-powerpoint,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,image/png,image/jpeg"
              onChange={handleInputChange}
              style={{ display: 'none' }}
            />

            <div
              className={`su-deck-dropzone ${isDragging ? 'dragging' : ''}`}
              onClick={handleDropzoneClick}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
            >
              <UploadIcon className="su-deck-dropzone-icon" />
              {selectedFile ? (
                <>
                  <div className="su-deck-dropzone-title">{selectedFile.name}</div>
                  <div className="su-deck-dropzone-subtitle">
                    {formatFileSize(selectedFile.size)} • Click to change
                  </div>
                </>
              ) : (
                <>
                  <div className="su-deck-dropzone-title">Drop your file here</div>
                  <div className="su-deck-dropzone-subtitle">or click to browse</div>
                </>
              )}
              <div className="su-deck-dropzone-hint">
                PDF, PowerPoint, Word, PNG, JPG supported (max 50MB)
              </div>
            </div>
          </div>

          <div className="su-deck-modal-footer">
            <button
              type="button"
              className="su-btn-secondary"
              onClick={onClose}
              disabled={isUploading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="su-btn-primary"
              disabled={isUploading || !selectedFile}
            >
              {isUploading ? 'Uploading...' : 'Upload'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );

  return ReactDOM.createPortal(modal, document.body);
};

export default UploadDeckModal;
